import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button, message, Modal } from "antd";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    Database,
    Eraser,
    Info,
    TableProperties,
    UploadCloud
} from "lucide-react";

import { getAdminAuthHeaders } from "../api/adminAuth";
import GenericTable from "../component/GenericTable";
import styles from "./Bm25Page.module.css";

const PERIOD_KEYS = ["period", "month", "monthPeriod", "periodName", "date"];
const SUPERSET_REQUIRED_HEADERS = [
    "BOM",
    "Дата создания поискового запроса",
    "Код клиента",
    "Наименование клиента",
    "Запрашиваемая номенклатура",
    "Запрошенное количество",
    "Предложенная номенклатура",
    "Количество",
    "Цена"
];
const SUPERSET_REQUIRED_COLUMNS_INFO = SUPERSET_REQUIRED_HEADERS.map((name, index) => ({
    position: index + 1,
    name,
    required: "Да"
}));
const SUPERSET_DATE_HEADER = "Дата создания поискового запроса";
const SUPERSET_FORMAT_DESCRIPTION = [
    "Файл должен быть CSV, разделитель колонок - точка с запятой (;).",
    "Первая строка должна содержать заголовки.",
    "Кодировка ожидается Windows-1251 или UTF-8.",
    "Дата создания поискового запроса должна быть в формате дд.мм.гггг.",
    "При загрузке период файла перезаписывается: старые строки за этот месяц удаляются и загружаются новые."
];

const withAdminAuth = (config = {}) => ({
    ...config,
    headers: {
        ...(config.headers || {}),
        ...getAdminAuthHeaders()
    }
});

const formatValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const getPeriodValue = (record) => {
    const directKey = PERIOD_KEYS.find(key => record[key] !== undefined && record[key] !== null);
    if (directKey) return String(record[directKey]);

    const firstStringKey = Object.keys(record).find(key => typeof record[key] === "string");
    return firstStringKey ? record[firstStringKey] : "";
};

const getColumnTitle = (key) => {
    const titles = {
        period: "Период",
        month: "Месяц",
        monthPeriod: "Период",
        periodName: "Период",
        date: "Дата",
        count: "Кол-во",
        total: "Всего",
        uploaded: "Загружено",
        deleted: "Удалено"
    };

    return titles[key] || key;
};

const normalizeHeader = (value) => String(value || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase();

const parseCsvLine = (line) => {
    const values = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === "\"") {
            if (inQuotes && nextChar === "\"") {
                current += "\"";
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (char === ";" && !inQuotes) {
            values.push(current.trim());
            current = "";
        } else {
            current += char;
        }
    }

    values.push(current.trim());
    return values;
};

const getCsvRecords = (text, limit = 25) => {
    const records = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        const nextChar = text[i + 1];

        if (char === "\"") {
            if (inQuotes && nextChar === "\"") {
                current += char + nextChar;
                i += 1;
            } else {
                inQuotes = !inQuotes;
                current += char;
            }
        } else if ((char === "\n" || char === "\r") && !inQuotes) {
            if (char === "\r" && nextChar === "\n") {
                i += 1;
            }
            if (current.trim()) {
                records.push(current);
            }
            current = "";
            if (records.length >= limit) {
                break;
            }
        } else {
            current += char;
        }
    }

    if (records.length < limit && current.trim()) {
        records.push(current);
    }

    return records;
};

const decodeSupersetFileCandidates = async (file) => {
    const buffer = await file.arrayBuffer();
    const candidates = [];

    try {
        candidates.push(new TextDecoder("windows-1251").decode(buffer));
    } catch {
        // Ignore unsupported legacy decoder and fall back to UTF-8 below.
    }

    candidates.push(new TextDecoder("utf-8").decode(buffer));
    return [...new Set(candidates)];
};

const isSupersetDate = (value) => /^\d{2}\.\d{2}\.\d{4}$/.test(String(value || "").trim());

export default function Bm25Page({ embedded = false }) {
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [lastResponse, setLastResponse] = useState(null);
    const fileInputRef = useRef(null);

    const validateSupersetFile = useCallback(async (file) => {
        const fileName = file?.name || "";
        if (!fileName.toLowerCase().endsWith(".csv")) {
            message.error("Файл Superset должен быть CSV с разделителем точка с запятой");
            return false;
        }

        let textCandidates = [];
        try {
            textCandidates = await decodeSupersetFileCandidates(file);
        } catch {
            message.error("Не удалось прочитать файл Superset");
            return false;
        }

        let records = [];
        let headers = [];
        let missingHeaders = SUPERSET_REQUIRED_HEADERS;
        for (const text of textCandidates) {
            const candidateRecords = getCsvRecords(text);
            const candidateHeaders = parseCsvLine(candidateRecords[0] || "");
            const normalizedHeaders = new Set(candidateHeaders.map(normalizeHeader));
            const candidateMissingHeaders = SUPERSET_REQUIRED_HEADERS.filter(header => !normalizedHeaders.has(normalizeHeader(header)));

            if (records.length === 0 || candidateMissingHeaders.length < missingHeaders.length) {
                records = candidateRecords;
                headers = candidateHeaders;
                missingHeaders = candidateMissingHeaders;
            }
        }

        if (records.length < 2) {
            message.error("CSV файл должен содержать строку заголовков и хотя бы одну строку данных");
            return false;
        }

        if (missingHeaders.length > 0) {
            message.error(`Неверный формат Superset: нет колонок ${missingHeaders.join(", ")}`);
            return false;
        }

        if (headers.length <= 1) {
            message.error("Неверный формат Superset: колонки должны быть разделены точкой с запятой");
            return false;
        }

        const dateColumnIndex = headers.findIndex(header => normalizeHeader(header) === normalizeHeader(SUPERSET_DATE_HEADER));
        const firstDataRows = records.slice(1).map(parseCsvLine).filter(row => row.some(Boolean));
        const hasValidDate = firstDataRows.some(row => isSupersetDate(row[dateColumnIndex]));
        if (!hasValidDate) {
            message.error("Неверный формат Superset: дата должна быть в формате дд.мм.гггг");
            return false;
        }

        return true;
    }, []);

    const loadPeriods = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get("/api/bm25/periods", withAdminAuth());
            setPeriods(Array.isArray(response.data) ? response.data : []);
        } catch {
            message.error("Ошибка загрузки периодов Superset");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPeriods();
    }, [loadPeriods]);

    const handleUpload = async () => {
        if (!selectedFile) {
            message.warning("Выберите файл для загрузки");
            return;
        }

        const isValidFile = await validateSupersetFile(selectedFile);
        if (!isValidFile) {
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);

        setUploading(true);
        try {
            const response = await axios.post("/api/bm25/upload", formData, withAdminAuth({
                headers: { "Content-Type": "multipart/form-data" }
            }));
            setLastResponse(response.data);
            message.success("Файл Superset загружен");
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            loadPeriods();
        } catch {
            message.error("Ошибка загрузки файла Superset");
        } finally {
            setUploading(false);
        }
    };

    const handleCleanBom = async () => {
        setCleaning(true);
        try {
            const response = await axios.get("/api/bom", withAdminAuth());
            setLastResponse(response.data);
            message.success("Очистка Superset запущена");
        } catch {
            message.error("Ошибка запуска очистки Superset");
        } finally {
            setCleaning(false);
        }
    };

    const handleDeletePeriod = useCallback((period) => {
        if (!period) {
            message.warning("Не удалось определить период для удаления");
            return;
        }

        Modal.confirm({
            title: "Удалить период Superset?",
            content: `Период: ${period}`,
            okText: "Удалить",
            cancelText: "Отмена",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const response = await axios.delete(`/api/bm25/periods/${encodeURIComponent(period)}`, withAdminAuth());
                    setLastResponse(response.data);
                    message.success("Период удалён");
                    loadPeriods();
                } catch {
                    message.error("Ошибка удаления периода");
                }
            }
        });
    }, [loadPeriods]);

    const handleShowFormatInfo = () => {
        Modal.info({
            title: (
                <div className={styles.supersetInfoTitle}>
                    <span><Info size={19} /></span>
                    <div>
                        <strong>Формат файла Superset</strong>
                        <small>Требования к загружаемому CSV</small>
                    </div>
                </div>
            ),
            icon: null,
            className: styles.supersetInfoModal,
            rootClassName: styles.supersetInfoModal,
            width: 820,
            style: { maxWidth: "calc(100vw - 32px)" },
            content: (
                <div className={styles.infoModalContent}>
                    <ul>
                        {SUPERSET_FORMAT_DESCRIPTION.map(item => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                    <p>Обязательные колонки:</p>
                    <table className={styles.infoTable}>
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>Название колонки</th>
                                <th>Обязательная</th>
                            </tr>
                        </thead>
                        <tbody>
                            {SUPERSET_REQUIRED_COLUMNS_INFO.map(column => (
                                <tr key={column.name}>
                                    <td>{column.position}</td>
                                    <td>{column.name}</td>
                                    <td>{column.required}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ),
            okText: "Ок"
        });
    };

    const columnsConfig = useMemo(() => {
        const keys = periods.length > 0
            ? Object.keys(periods[0])
            : ["period"];

        const columns = keys.map(key => ({
            title: getColumnTitle(key),
            dataIndex: key,
            key,
            sortable: true,
            searchable: PERIOD_KEYS.includes(key),
            render: value => formatValue(value)
        }));

        columns.push({
            title: "Действие",
            key: "action",
            render: (_, record) => (
                <Button
                    danger
                    onClick={() => handleDeletePeriod(getPeriodValue(record))}
                >
                    Удалить
                </Button>
            )
        });

        return columns;
    }, [handleDeletePeriod, periods]);

    return (
        <main className={embedded ? styles.embeddedPage : styles.page}>
            <div className={styles.shell}>
                <header className={styles.header}>
                    <span className={styles.headerVisual} aria-hidden="true">
                        <Database size={25} />
                    </span>
                    <div className={styles.titleBlock}>
                        <span className={styles.eyebrow}>Данные и аналитика</span>
                        <h1 className={styles.title}>Загрузка в Superset</h1>
                        <p className={styles.subtitle}>Загрузка базы, просмотр периодов и удаление месячных срезов.</p>
                    </div>

                    {!embedded && (
                        <Link className={styles.backLink} to="/">
                            <ArrowLeft size={16} />
                            Анализ цен
                        </Link>
                    )}
                </header>

                <div className={styles.grid}>
                    <section className={styles.panel}>
                        <div className={styles.panelTitleRow}>
                            <div className={styles.panelHeading}>
                                <span className={styles.panelIcon}><UploadCloud size={20} /></span>
                                <div>
                                    <h2 className={styles.panelTitle}>Загрузка и обслуживание</h2>
                                    <p className={styles.panelText}>Загрузите CSV Superset или запустите очистку данных.</p>
                                </div>
                            </div>
                            <button
                                className={styles.infoButton}
                                type="button"
                                onClick={handleShowFormatInfo}
                            >
                                <Info size={16} />
                                Формат файла
                            </button>
                        </div>
                        <div className={styles.actions}>
                            <button
                                className={styles.primaryButton}
                                type="button"
                                disabled={uploading}
                                onClick={handleUpload}
                            >
                                <UploadCloud size={16} />
                                {uploading ? "Загрузка..." : "Загрузить"}
                            </button>

                            <button
                                className={styles.secondaryButton}
                                type="button"
                                disabled={cleaning}
                                onClick={handleCleanBom}
                            >
                                <Eraser size={16} />
                                {cleaning ? "Очистка..." : "Очистить"}
                            </button>
                        </div>
                        <input
                            ref={fileInputRef}
                            className={styles.fileInput}
                            type="file"
                            accept=".csv"
                            onChange={event => setSelectedFile(event.target.files?.[0] || null)}
                        />

                        {lastResponse !== null && (
                            <pre className={styles.resultBox}>
                                {formatValue(lastResponse)}
                            </pre>
                        )}
                    </section>

                    <section className={styles.panel}>
                        <div className={styles.tableHeader}>
                            <div className={styles.panelHeading}>
                                <span className={`${styles.panelIcon} ${styles.panelIconSuccess}`}>
                                    <TableProperties size={20} />
                                </span>
                                <div>
                                    <h2 className={styles.panelTitle}>Реестр документов</h2>
                                    <p className={styles.panelText}>Загруженные месячные срезы базы.</p>
                                </div>
                            </div>
                            <span className={styles.counter}>{periods.length} записей</span>
                        </div>

                        <GenericTable
                            data={periods}
                            loading={loading}
                            columnsConfig={columnsConfig}
                            rowKey={(record) => getPeriodValue(record) || JSON.stringify(record)}
                            scroll={{ x: "max-content" }}
                        />
                    </section>
                </div>
            </div>
        </main>
    );
}
